// src/utils/otaParser.js

// ─── OTA 예약 정보 파싱 엔진 ───
// 목표: 파싱 정확도 80%+ / "복붙→발송" 15초 이내
// 전화번호+체크인 날짜 자동 추출 기준 성공률 70%+

/**
 * OTA 예약 텍스트를 파싱하여 구조화된 데이터 반환
 * @param {string} text - OTA에서 복사한 예약 정보 텍스트
 * @returns {ParseResult}
 */
export function parseOTAText(text) {
  if (!text || !text.trim()) return emptyResult();

  const result = {
    name: '',
    phone: '',
    checkin: '',
    checkout: '',
    room: '',
    guests: '',
    guestsDetail: { adult: 0, child: 0, infant: 0 },
    channel: '',
    bookingId: '',
    amount: '',
    confidence: {},
    rawText: text,
  };

  // 채널 감지 (가장 먼저 - 채널별 파싱 힌트에 활용)
  result.channel = detectChannel(text);

  // 예약번호
  const bookingId = extractBookingId(text);
  if (bookingId) result.bookingId = bookingId;

  // 전화번호 (최우선)
  const phone = extractPhone(text);
  if (phone) {
    result.phone = phone.value;
    result.confidence.phone = phone.confidence;
  }

  // 날짜
  const dates = extractDates(text);
  if (dates.checkin) {
    result.checkin = dates.checkin;
    result.confidence.checkin = dates.checkinConf;
  }
  if (dates.checkout) {
    result.checkout = dates.checkout;
    result.confidence.checkout = dates.checkoutConf;
  }

  // 인원
  const guests = extractGuests(text);
  if (guests) {
    result.guests = String(guests.total);
    result.guestsDetail = guests.detail;
    result.confidence.guests = guests.confidence;
  }

  // 객실
  const room = extractRoom(text);
  if (room) {
    result.room = room.value;
    result.confidence.room = room.confidence;
  }

  // 이름
  const name = extractName(text);
  if (name) {
    result.name = name.value;
    result.confidence.name = name.confidence;
  }

  // 금액
  const amount = extractAmount(text);
  if (amount) result.amount = amount;

  return result;
}

// ─── 날짜 유효성 검증 (2월 30일 같은 불가능한 날짜 차단) ───
function isValidDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return false;
  // padStart 후 월/일이 실제 날짜와 일치하는지 확인
  const [year, month, day] = dateStr.split('-').map(Number);
  return d.getFullYear() === year && d.getMonth() + 1 === month && d.getDate() === day;
}

// ─── 빈 결과 ───
function emptyResult() {
  return {
    name: '', phone: '', checkin: '', checkout: '', room: '',
    guests: '', guestsDetail: { adult: 0, child: 0, infant: 0 },
    channel: '', bookingId: '', amount: '',
    confidence: {}, rawText: '',
  };
}

// ─── 채널 감지 ───
function detectChannel(text) {
  const t = text.toLowerCase();
  if (/야놀자|yanolja/i.test(t)) return '야놀자';
  if (/에어비앤비|airbnb/i.test(t)) return '에어비앤비';
  if (/네이버|naver/i.test(t)) return '네이버예약';
  if (/여기어때/i.test(t)) return '여기어때';
  if (/부킹|booking\.com/i.test(t)) return '부킹닷컴';
  if (/오늘밤엔/i.test(t)) return '오늘밤엔';
  if (/호텔스컴바인|hotelscombined/i.test(t)) return '호텔스컴바인';
  if (/아고다|agoda/i.test(t)) return '아고다';
  if (/펜션마루/i.test(t)) return '펜션마루';
  return '';
}

// ─── 전화번호 추출 ───
function extractPhone(text) {
  // 010 번호 (우선)
  const mobile = text.match(/(?:연락처|전화|휴대폰|핸드폰|H\.P|HP|Tel)?\s*:?\s*(010[-.\s]?\d{3,4}[-.\s]?\d{4})/i);
  if (mobile) {
    return { value: formatPhone(mobile[1]), confidence: 'high' };
  }

  // 010 번호 (라벨 없이)
  const bare010 = text.match(/\b(010[-.\s]?\d{3,4}[-.\s]?\d{4})\b/);
  if (bare010) {
    return { value: formatPhone(bare010[1]), confidence: 'high' };
  }

  // 050 안심번호
  const safe050 = text.match(/(?:연락처|전화)?\s*:?\s*(050\d[-.\s]?\d{3,4}[-.\s]?\d{4})/i);
  if (safe050) {
    return { value: formatPhone050(safe050[1]), confidence: 'high' };
  }

  // 050 번호 (라벨 없이)
  const bare050 = text.match(/\b(050\d[-.\s]?\d{3,4}[-.\s]?\d{4})\b/);
  if (bare050) {
    return { value: formatPhone050(bare050[1]), confidence: 'medium' };
  }

  return null;
}

function formatPhone(raw) {
  const digits = raw.replace(/[-.\s]/g, '');
  if (digits.length === 11) return `${digits.slice(0,3)}-${digits.slice(3,7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
  return raw;
}

function formatPhone050(raw) {
  const digits = raw.replace(/[-.\s]/g, '');
  // 0502-XXXX-XXXX (12자리)
  if (digits.length === 12) return `${digits.slice(0,4)}-${digits.slice(4,8)}-${digits.slice(8)}`;
  return raw;
}

// ─── 날짜 추출 ───
function extractDates(text) {
  const result = { checkin: '', checkout: '', checkinConf: '', checkoutConf: '' };
  const dates = [];

  // YYYY-MM-DD 또는 YYYY.MM.DD 또는 YYYY/MM/DD
  const isoPattern = /(\d{4})[.\-/](\d{1,2})[.\-/](\d{1,2})/g;
  let m;
  while ((m = isoPattern.exec(text)) !== null) {
    const dateStr = `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
    if (isValidDate(dateStr)) {
      dates.push({ value: dateStr, index: m.index, format: 'iso' });
    }
  }

  // YY.MM.DD (2자리 연도) — 현재 연도±2년 범위만 허용
  const currentYY = new Date().getFullYear() % 100;
  const shortYearPattern = /(\d{2})[.\-/](\d{1,2})[.\-/](\d{1,2})/g;
  while ((m = shortYearPattern.exec(text)) !== null) {
    const year = parseInt(m[1]);
    if (year >= currentYY - 1 && year <= currentYY + 2) {
      const fullYear = `20${m[1]}`;
      const dateStr = `${fullYear}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
      if (isValidDate(dateStr) && !dates.find(d => d.value === dateStr)) {
        dates.push({ value: dateStr, index: m.index, format: 'short' });
      }
    }
  }

  // MM월 DD일
  const korPattern = /(\d{1,2})월\s*(\d{1,2})일/g;
  while ((m = korPattern.exec(text)) !== null) {
    const month = m[1].padStart(2, '0');
    const day = m[2].padStart(2, '0');
    const year = new Date().getFullYear();
    const dateStr = `${year}-${month}-${day}`;
    if (isValidDate(dateStr) && !dates.find(d => d.value === dateStr)) {
      dates.push({ value: dateStr, index: m.index, format: 'korean' });
    }
  }

  // 날짜 정렬 (텍스트 내 위치 기준)
  dates.sort((a, b) => a.index - b.index);

  // "예약일", "접수일", "주문일" 라벨 근처 날짜는 체크인/체크아웃 후보에서 제외
  const reservationDateIdx = findLabelNearDate(text, dates, ['예약일', '접수일', '주문일', '신청일']);
  const candidateDates = dates.filter((_, i) => i !== reservationDateIdx);

  // 체크인/체크아웃 판별
  if (candidateDates.length >= 2) {
    const checkinIdx = findLabelNearDate(text, candidateDates, ['이용일', '체크인', '입실', 'check-in', 'checkin']);
    const checkoutIdx = findLabelNearDate(text, candidateDates, ['체크아웃', '퇴실', 'check-out', 'checkout']);

    if (checkinIdx >= 0 && checkoutIdx >= 0 && checkinIdx !== checkoutIdx) {
      result.checkin = candidateDates[checkinIdx].value;
      result.checkout = candidateDates[checkoutIdx].value;
      result.checkinConf = 'high';
      result.checkoutConf = 'high';
    } else if (checkinIdx >= 0) {
      result.checkin = candidateDates[checkinIdx].value;
      result.checkinConf = 'high';
      // 체크인 다음 날짜를 체크아웃으로
      const next = candidateDates.find((_, i) => i !== checkinIdx);
      if (next) { result.checkout = next.value; result.checkoutConf = 'medium'; }
    } else {
      // 라벨 없으면 첫 번째=체크인, 두 번째=체크아웃
      result.checkin = candidateDates[0].value;
      result.checkout = candidateDates[1].value;
      result.checkinConf = 'medium';
      result.checkoutConf = 'medium';
    }
  } else if (candidateDates.length === 1) {
    result.checkin = candidateDates[0].value;
    result.checkinConf = 'medium';
    const d = new Date(candidateDates[0].value);
    d.setDate(d.getDate() + 1);
    result.checkout = d.toISOString().split('T')[0];
    result.checkoutConf = 'guess';
  }

  // N박 패턴으로 체크아웃 보정 (checkout이 없거나 신뢰도 낮을 때)
  const nightMatch = text.match(/(\d+)박/);
  if (nightMatch && result.checkin && (!result.checkout || result.checkoutConf !== 'high')) {
    const nights = parseInt(nightMatch[1]);
    const d = new Date(result.checkin);
    d.setDate(d.getDate() + nights);
    result.checkout = d.toISOString().split('T')[0];
    result.checkoutConf = 'high';
  }

  return result;
}

function findLabelNearDate(text, dates, labels) {
  for (const label of labels) {
    const labelIdx = text.toLowerCase().indexOf(label.toLowerCase());
    if (labelIdx >= 0) {
      // 라벨에서 가장 가까운 날짜 찾기
      let closest = -1;
      let minDist = Infinity;
      dates.forEach((d, i) => {
        const dist = Math.abs(d.index - labelIdx);
        if (dist < minDist) { minDist = dist; closest = i; }
      });
      if (closest >= 0 && minDist < 100) return closest;
    }
  }
  return -1;
}

// ─── 인원 추출 ───
function extractGuests(text) {
  // "성인:8명/아동:0명/유아:0명" 패턴
  const detailMatch = text.match(/성인\s*:?\s*(\d+)\s*명?\s*[/,]\s*아동\s*:?\s*(\d+)\s*명?\s*[/,]\s*유아\s*:?\s*(\d+)\s*명?/);
  if (detailMatch) {
    const adult = parseInt(detailMatch[1]);
    const child = parseInt(detailMatch[2]);
    const infant = parseInt(detailMatch[3]);
    return {
      total: adult + child + infant,
      detail: { adult, child, infant },
      confidence: 'high',
    };
  }

  // "예약인원 8명" 또는 "인원 : 8명"
  const totalMatch = text.match(/(?:예약\s*)?인원\s*[:：]?\s*(\d+)\s*명/);
  if (totalMatch) {
    const total = parseInt(totalMatch[1]);
    return { total, detail: { adult: total, child: 0, infant: 0 }, confidence: 'high' };
  }

  // "성인 4명"
  const adultOnly = text.match(/성인\s*(\d+)\s*명/);
  if (adultOnly) {
    const adult = parseInt(adultOnly[1]);
    return { total: adult, detail: { adult, child: 0, infant: 0 }, confidence: 'medium' };
  }

  // "N명" (단독)
  const bareMatch = text.match(/\b(\d{1,2})\s*명\b/);
  if (bareMatch) {
    const total = parseInt(bareMatch[1]);
    if (total > 0 && total <= 50) {
      return { total, detail: { adult: total, child: 0, infant: 0 }, confidence: 'medium' };
    }
  }

  return null;
}

// ─── 객실명 추출 ───

// 객실명 앞에 붙는 불필요한 라벨 텍스트
const ROOM_LABEL_NOISE = /^(?:추가\s*연락처|추가연락처|예약\s*객실|객실명?|상품명?|룸|방)\s*/;

function cleanRoomValue(raw) {
  return raw.trim().replace(ROOM_LABEL_NOISE, '').trim().slice(0, 40);
}

function extractRoom(text) {
  // "객실 : 힐링독채 A" — 콜론 있는 경우
  const withColon = text.match(/(?:예약\s*객실|객실명?|상품명?|룸|방)\s*[:：]\s*([^\n\r]+)/);
  if (withColon) {
    return { value: cleanRoomValue(withColon[1]), confidence: 'high' };
  }

  // "예약객실 햇살마루(2층독채)" — 콜론 없이 공백으로 구분된 경우
  // 라벨 바로 뒤 한글+괄호 패턴 포착
  const withSpace = text.match(/(?:예약\s*객실|객실명?)\s+([가-힣A-Za-z0-9\s()（）]{2,30})/);
  if (withSpace) {
    const val = cleanRoomValue(withSpace[1]);
    if (val.length >= 2) return { value: val, confidence: 'high' };
  }

  // "햇살마루(2층독채)" 패턴 — 괄호 포함 독립 객실명
  const parenRoom = text.match(/([가-힣]{2,10})\s*[(\（]([^)\）\n]{1,20})[)\）]/);
  if (parenRoom && /독채|동|호|룸|스위트|테라스|캐빈|층/.test(parenRoom[2])) {
    return { value: `${parenRoom[1]}(${parenRoom[2]})`, confidence: 'medium' };
  }

  return null;
}

// ─── 예약자명 추출 ───

// 이름 라벨 패턴: 줄 앞이 이 패턴이면 "이름 라벨 줄"로 인식
const NAME_LABEL_RE = /^(?:대표예약자|예약자명|예약자|투숙객|고객명|성명|이름|손님|성함)\s*[:：]?\s*/;

// 한글 이름 형식: 2~5자, 공백 포함 이름도 허용
const KOREAN_NAME_RE = /^[가-힣]{2,5}(?:\s[가-힣]{1,3})?$/;

// 객실명·플랫폼명 등 "포함" 금지 패턴
const NAME_EXCLUDE_RE = /독채|펜션|힐링|객실|스위트|디럭스|패밀리|커플|야놀자|에어비앤비|네이버|여기어때|배너|호텔|리조트|빌라|풀빌라|카라반|전화예약|직접예약|현장예약|온라인예약|앱예약|방문예약/;

// ─── 절대 이름이 될 수 없는 라벨/항목명 (완전 일치) ───
// "번호", "연락처" 등 2~3글자 라벨이 한글 이름 형식을 통과하는 것을 차단
const NAME_FORBIDDEN = new Set([
  '번호', '연락처', '전화번호', '예약번호', '고객명', '예약자명', '이름', '성함',
  '이메일', '인원', '객실', '체크인', '체크아웃', '성명', '투숙객', '대표예약자',
  '손님', '주소', '날짜', '기간', '박수', '결제', '금액', '입금', '확인',
  '예약자', '예약일', '입실', '퇴실', '접수', '주문', '상품명', '상품', '룸',
  '방', '호실', '비밀번호', '패스워드', '정보', '채널', '경로', '구분',
  '신규예약', '오늘밤엔', '전화', '직접', '현장',
]);

const isDev = process.env.NODE_ENV === 'development';

/**
 * 이름 후보 유효성 검사 (모든 조건 동시 적용)
 * - 금지어 완전 일치 제외
 * - 한글 2~5자 형식 미충족 제외
 * - 객실/플랫폼명 포함 패턴 제외
 * - 숫자·하이픈·콜론 포함 제외 (전화번호 등)
 */
function isValidName(candidate) {
  if (!candidate) return false;
  if (NAME_FORBIDDEN.has(candidate)) return false;
  if (!KOREAN_NAME_RE.test(candidate)) return false;
  if (NAME_EXCLUDE_RE.test(candidate)) return false;
  if (/[\d\-:]/.test(candidate)) return false;
  return true;
}

/**
 * 최종 방어선: 결과 값이 금지어이거나 숫자가 섞인 경우 null 반환
 * isValidName을 통과했더라도 마지막으로 한 번 더 검증
 */
function sanitizeName(value) {
  if (!value) return null;
  if (NAME_FORBIDDEN.has(value)) return null;
  if (/\d{2,}/.test(value)) return null;   // 숫자 2자리 이상 포함
  if (/[-:]/.test(value)) return null;      // 하이픈/콜론 포함 (전화번호 잔재)
  return value;
}

function extractName(text) {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);

  if (isDev) {
    console.debug('[이름파싱] ─────────────────────────────');
    console.debug('[이름파싱] OCR 원문 (%d줄):\n%s', lines.length, text);
    console.debug('[이름파싱] ─────────────────────────────');
  }

  const debugCandidates = [];
  const debugRejected   = [];

  function tryName(value, source) {
    if (!value) return false;
    if (isDev) debugCandidates.push({ value, source });
    if (isValidName(value)) return true;
    if (isDev) {
      const reason =
        NAME_FORBIDDEN.has(value)    ? '금지어(완전일치)' :
        !KOREAN_NAME_RE.test(value)  ? '이름형식아님' :
        NAME_EXCLUDE_RE.test(value)  ? '금지패턴포함' :
        /[\d\-:]/.test(value)        ? '숫자/특수문자' : '기타';
      debugRejected.push({ value, source, reason });
    }
    return false;
  }

  // ── 명시적 라벨 기반 탐색만 허용 ──
  // fallback(전화번호 주변, 님 패턴 등) 사용 안 함
  // "틀린 이름 자동입력보다 미감지가 낫다"

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!NAME_LABEL_RE.test(line)) continue;

    if (isDev) console.debug('[이름파싱] 라벨 줄 발견 [%d]: "%s"', i, line);

    // ① 같은 줄 값: "예약자 : 한아름" / "예약자명:한아름"
    //    2열 표 OCR "예약자명 번호" → afterLabel="번호" → 금지어로 차단
    const afterLabel = line.replace(NAME_LABEL_RE, '').trim();
    if (afterLabel) {
      if (tryName(afterLabel, '①같은줄')) {
        const clean = sanitizeName(afterLabel);
        if (clean) {
          if (isDev) console.debug('[이름파싱] ① 최종 선택: "%s"', clean);
          return { value: clean, confidence: 'high' };
        }
      }
      // 마스킹 이름: "한아*"
      if (/^[가-힣]{1,3}[*＊]{1,2}$/.test(afterLabel) && !NAME_FORBIDDEN.has(afterLabel)) {
        if (isDev) console.debug('[이름파싱] ① 마스킹 선택: "%s"', afterLabel);
        return { value: afterLabel, confidence: 'medium' };
      }
    }

    // ② 다음 줄 탐색 (최대 3줄)
    //    핵심 수정: 금지어 줄은 break 대신 continue → 그 뒤 줄도 계속 확인
    //
    //    수정 전 (버그):
    //      예약자명 → j+1: "번호" → tryName 실패, truthy → break → "한아름" 못 봄
    //    수정 후:
    //      예약자명 → j+1: "번호" → NAME_FORBIDDEN → continue → j+2: "한아름" → 선택
    for (let j = i + 1; j <= i + 3 && j < lines.length; j++) {
      const next = lines[j];

      // 금지어 줄은 라벨로 간주 — 스킵하고 계속 탐색
      if (NAME_FORBIDDEN.has(next)) {
        if (isDev) console.debug('[이름파싱] ② 금지어 줄 스킵 [%d]: "%s"', j, next);
        continue;
      }

      // 줄 전체가 이름
      if (tryName(next, `②다음줄[${j}]전체`)) {
        const clean = sanitizeName(next);
        if (clean) {
          if (isDev) console.debug('[이름파싱] ② 다음줄 전체 선택: "%s"', clean);
          return { value: clean, confidence: 'high' };
        }
      }

      // 2열 표 OCR: "한아름 0502-4045-7388" → 줄 앞 한글만 추출
      const korStart = next.match(/^([가-힣]{2,5}(?:\s[가-힣]{1,3})?)/)?.[1]?.trim();
      if (korStart && tryName(korStart, `②앞한글[${j}]`)) {
        const clean = sanitizeName(korStart);
        if (clean) {
          if (isDev) console.debug('[이름파싱] ② 앞한글 선택: "%s"', clean);
          return { value: clean, confidence: 'medium' };
        }
      }

      // 금지어도 아니고 이름도 아닌 줄 → 더 이상 관련 없음, 탐색 중단
      if (next) break;
    }
  }

  // fallback ③ (전화번호 주변 추정), ④ (님 패턴) 제거
  // 라벨에서 명시적으로 찾지 못하면 미감지 처리

  if (isDev) {
    console.debug('[이름파싱] 후보 목록:', debugCandidates);
    console.debug('[이름파싱] 금지어 제외:', debugRejected);
    console.debug('[이름파싱] 최종: 미감지 (보수 모드 — fallback 없음)');
  }

  return null;
}

// ─── 이름 파싱 테스트 (개발/디버그용) ───
// 브라우저 콘솔에서 import 후 runNameParseTests() 로 호출
export function runNameParseTests() {
  const PASS = '✅ PASS';
  const FAIL = '❌ FAIL';

  const cases = [
    // ── "번호"가 이름으로 들어가는 케이스 (반드시 실패해야 함) ──
    {
      desc: '2열 표: "예약자명 번호 / 한아름 0502-..." → 한아름',
      text: '예약자명 번호\n한아름 0502-4045-7388',
      allowed: ['한아름'],
      forbidden: ['번호'],
    },
    {
      desc: '세로 나열: "예약자명 / 번호 / 한아름" → 한아름',
      text: '예약자명\n번호\n한아름\n0502-4045-7388',
      allowed: ['한아름'],
      forbidden: ['번호'],
    },
    {
      desc: '"예약자명" 뒤 "번호"만 있고 이름 없음 → 미감지',
      text: '예약자명\n번호\n체크인\n2026-07-15',
      allowed: [''],   // null → ''
      forbidden: ['번호', '체크인'],
    },
    // ── 정상 케이스 ──
    {
      desc: '같은 줄 이름: "예약자 : 한아름" → 한아름',
      text: '예약번호 : YN-001\n예약자 : 한아름\n연락처 : 010-3456-7890',
      allowed: ['한아름'],
      forbidden: ['번호', '연락처'],
    },
    {
      desc: '라벨 다음 줄 이름: "예약자명\\n한아름" → 한아름',
      text: '예약자명\n한아름\n010-1234-5678',
      allowed: ['한아름'],
      forbidden: ['번호'],
    },
    {
      desc: '마스킹 이름: "예약자명:한아*" → 한아*',
      text: '예약자명:한아*\n0502-1234-5678',
      allowed: ['한아*'],
      forbidden: ['번호'],
    },
    {
      desc: '이름 없음 → 미감지',
      text: '체크인 : 2026-07-15\n체크아웃 : 2026-07-16\n인원 : 4명',
      allowed: [''],
      forbidden: ['번호', '체크인', '인원'],
    },
    {
      desc: '"연락처"가 이름으로 들어가지 않아야 함',
      text: '예약자명\n연락처\n한아름\n010-9999-1234',
      allowed: ['한아름'],
      forbidden: ['번호', '연락처'],
    },
    {
      desc: '"번호"로만 구성된 텍스트 → 미감지',
      text: '번호\n연락처\n010-1234-5678',
      allowed: [''],
      forbidden: ['번호', '연락처'],
    },
  ];

  let passed = 0;
  let failed = 0;
  const results = [];

  for (const tc of cases) {
    const result = extractName(tc.text);
    const value  = result?.value ?? '';

    const forbiddenHit = tc.forbidden.find(f => value === f);
    const allowedMatch = tc.allowed.includes(value);
    const ok = allowedMatch && !forbiddenHit;

    if (ok) passed++;
    else    failed++;

    results.push({
      result: ok ? PASS : FAIL,
      desc:   tc.desc,
      got:    `"${value}"`,
      expected: tc.allowed.map(a => `"${a}"`).join(' or '),
      ...(forbiddenHit ? { forbidden_hit: `"${forbiddenHit}"` } : {}),
    });
  }

  console.table(results);
  console.log(`\n이름 파싱 테스트: ${passed}/${cases.length} 통과${failed > 0 ? ` (${failed}개 실패)` : ' 🎉'}`);
  return { passed, failed, total: cases.length };
}

// ─── 예약번호 추출 ───
function extractBookingId(text) {
  const patterns = [
    /예약\s*번호\s*[:：]\s*([A-Za-z0-9_-]+)/,
    /(?:booking|reservation)\s*(?:id|no|#)\s*[:：]?\s*([A-Za-z0-9_-]+)/i,
    /\b([A-Z]{2,4}[-_]\d{8,}[-_]\d+)\b/, // YN-20260715-8834 패턴
    /\b(\d{10,}_\d+)\b/, // 1774781775_564319 패턴
  ];

  for (const pat of patterns) {
    const m = text.match(pat);
    if (m) return m[1];
  }
  return '';
}

// ─── 금액 추출 ───
function extractAmount(text) {
  const patterns = [
    /(?:결제|금액|실금액|총액|합계)\s*[:：]?\s*([\d,]+)\s*원/,
    /([\d,]+)\s*원\s*\(실금액/,
    /([\d,]+)원/,
  ];

  for (const pat of patterns) {
    const m = text.match(pat);
    if (m) {
      const amount = m[1].replace(/,/g, '');
      if (parseInt(amount) > 10000) return amount; // 만원 이상만
    }
  }
  return '';
}

// ─── 파싱 결과 요약 ───
export function getParseScore(result) {
  let score = 0;
  let total = 0;

  const fields = ['phone', 'checkin', 'checkout', 'name', 'room', 'guests'];
  for (const f of fields) {
    total++;
    const conf = result.confidence[f];
    if (conf === 'high') score += 1;
    else if (conf === 'medium') score += 0.7;
    else if (conf === 'guess') score += 0.3;
  }

  return Math.round((score / total) * 100);
}

// ─── 050 번호 감지 ───
export function is050Number(phone) {
  if (!phone) return false;
  return /^050\d/.test(phone.replace(/[-\s]/g, ''));
}
